import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DriverResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const token = searchParams.get('token');
  
  // Password validation states
  const [validations, setValidations] = useState({
    minLength: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSymbol: false,
    passwordsMatch: false
  });

  useEffect(() => {
    if (!token) {
      toast.error(t('reset_toast_invalid_token'));
      navigate('/driver/login');
    }
  }, [token, navigate]);
  
  // Validate password in real-time
  useEffect(() => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const passwordsMatch = password === confirmPassword && password.length > 0;
    
    setValidations({
      minLength,
      hasUpperCase,
      hasNumber,
      hasSymbol,
      passwordsMatch
    });
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('change_password_mismatch'));
      return;
    }

    // Validate password requirements
    if (!validations.minLength || !validations.hasUpperCase || !validations.hasNumber || !validations.hasSymbol) {
      toast.error(t('reset_toast_password_requirements'));
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('new_password', password);
      
      const response = await fetch(`${BACKEND_URL}/api/auth/driver/reset-password`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        let errorMessage = t('reset_toast_error');
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map(err => err.msg || err).join(', ');
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        }
        throw new Error(errorMessage);
      }

      toast.success(t('reset_toast_success'));
      setTimeout(() => {
        navigate('/driver/login');
      }, 1500);
      
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.message || t('reset_toast_error');
      toast.error(typeof errorMessage === 'string' ? errorMessage : t('reset_toast_error'));
    } finally {
      setLoading(false);
    }
  };

  const ValidationItem = ({ valid, text }) => (
    <div className="flex items-center gap-2 text-sm">
      {valid ? (
        <CheckCircle className="w-4 h-4 text-green-500" />
      ) : (
        <XCircle className="w-4 h-4 text-gray-300" />
      )}
      <span className={valid ? 'text-green-600' : 'text-gray-500'}>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center" style={{
      background: 'linear-gradient(135deg, #0a3042 0%, #0d4559 50%, #0a3042 100%)'
    }}>
      <div className="w-full max-w-md mx-auto px-6">
        <Card className="glass-card" style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <CardHeader className="text-center py-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_927b78ff-6137-4209-a5c6-1343491e5188/artifacts/04fvidd7_1c.png" 
                alt="AutoGo Icon" 
                className="h-12 w-12"
              />
              <span className="text-4xl font-bold" style={{color: '#0a3042'}}>AutoGo</span>
            </div>
            <CardTitle className="text-2xl font-bold" style={{color: '#0a3042'}}>
              {t('reset_title')}
            </CardTitle>
            <CardDescription>
              {t('driver_reset_description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium" style={{color: '#0a3042'}}>
                  {t('reset_new_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11"
                    placeholder={t('ph_new_password_placeholder')}
                    required
                    data-testid="driver-reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium" style={{color: '#0a3042'}}>
                  {t('reset_confirm_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 h-11"
                    placeholder={t('reset_confirm_password')}
                    required
                    data-testid="driver-reset-password-confirm-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Password requirements */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-gray-700 mb-2">{t('reset_requirements_title')}</p>
                <ValidationItem valid={validations.minLength} text={t('reset_req_min_length')} />
                <ValidationItem valid={validations.hasUpperCase} text={t('reset_req_uppercase')} />
                <ValidationItem valid={validations.hasNumber} text={t('reset_req_number')} />
                <ValidationItem valid={validations.hasSymbol} text={t('reset_req_symbol')} />
                <ValidationItem valid={validations.passwordsMatch} text={t('reset_passwords_match')} />
              </div>

              <Button
                type="submit"
                disabled={loading || !Object.values(validations).every(Boolean)}
                className="w-full h-11 text-base font-semibold"
                style={{
                  backgroundColor: '#36e961',
                  color: '#0a3042'
                }}
                data-testid="driver-reset-password-submit"
              >
                {loading ? t('reset_resetting') : t('reset_submit')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Button
                variant="link"
                onClick={() => navigate('/driver/login')}
                className="text-sm"
                style={{color: '#0a3042'}}
              >
                {t('reset_back_login')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverResetPassword;
